import { React, Box, Grid2, useState, useEffect, Typography, useParams, dayjs, Dayjs, Default_Text_Color, CircularProgress } from '../../utils/globalImports';
import { CustomTextField, CustomButton, FilterCard, useApi, CustomDataTable, validationMessages, Toaster } from '../../utils/globalImports';
import { ApiResponse } from '../../services/apiService';

interface Note {
  noteId?: number;
  resourceId?: number;
  notes: string;
  createdId?: number;
  createdName?: string;
  updatedId?: number;
  updatedName?: string;
  createdAt?: Dayjs;
  updatedAt?: Dayjs;
  status?: number;
}

interface NotesComponentProps {
  entityTypeId?: number; // Project, Proposal, FeeStage
  entityId?: number; // Id of project
  resourceId: number;
  title: string;
  searchbarText: string;
}

const NotesComponent: React.FC<NotesComponentProps> = ({
  entityTypeId,
  entityId,
  resourceId,
  title,
  searchbarText
}) => {
  const [selectedStatus,setSelectedStatus]=useState<any[]>([1]);
  const [noteDataList, setNoteDataList] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [formData, setFormData] = useState<Note>({ notes: '' });
  const [selectedNoteRow, setSelectedNoteRow] = useState<any | null>(null);
  const [errors, setErrors] = useState({ description: '' });
  const [toast, setToast] = useState({ open: false, message: "", type: "success" });
  const [isOn, setIsOn] = useState<boolean>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { execute, loading } = useApi();
  const [dataLoaded, setDataLoaded] = useState(false);

  const userId = Number(localStorage.getItem("userId"));

  const sortOptions = [
    { value: "Created date_desc", text: "Latest" },
    { value: "Created date_asc", text: "Oldest" },
  ];



  useEffect(() => {
    fetchAllNotes();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if(errors.description){
      setErrors({description:''})
    }
  };

  const fetchAllNotes = async () => {
    const { data } = await execute<any>('get', `Note?entityTypeId=${entityTypeId}&entityId=${entityId}`);

    const mappedNotes: Note[] = data.map((item: any) => ({
      noteId: item.noteId,
      resourceId: item.entityId,
      notes: item.notes,
      createdId: item.createdId,
      updatedId: item.updatedId,
      status: item.status,
      createdAt: item.createdAt ? dayjs.utc(item.createdAt) : undefined,
      updatedAt: item.updatedAt ? dayjs.utc(item.updatedAt) : undefined,
      createdName: item.createdName, // Optional if provided by API
      updatedName: item.updatedName  // Optional if provided by API
    }));

    if (Array.isArray(data)) {
      setNoteDataList(mappedNotes);
      
      showGrid(mappedNotes,selectedStatus);
    }
    setDataLoaded(true);
  };

  const showGrid = (dataList: any[], status = [1]) => {
    const activeNotes = dataList
      .filter((note) => status.includes(note.status ?? 1))
      .map((note, index) => {
        const id = note.noteId; // dynamic key: proposalNoteId / projectNoteId
        return {
          id: id ?? `note-${index}`,
          resourceId: note.entityId,  // dynamic key: proposalId / projectId
          "Notes/Comments": note.notes,
          "Last edited by": note.updatedName,
          "Last edited date": note.updatedAt && !dayjs(note.updatedAt).isSame("0001-01-01T00:00:00", "second") ? dayjs(note.updatedAt).local().format("DD/MM/YYYY HH:mm:ss") : "",
          "Created date": note.createdAt ? dayjs.utc(note.createdAt).local().format("DD/MM/YYYY HH:mm:ss") : "",
          "Created by": note.createdName,
          Status: note.status === 1 ? 'Active' : 'Inactive'
        };
      })
      .sort((a, b) =>
        dayjs(b["Created date"], "DD/MM/YYYY HH:mm:ss").valueOf() -
        dayjs(a["Created date"], "DD/MM/YYYY HH:mm:ss").valueOf()
      );

    setFilteredData(activeNotes);
  };


  const handleSearchGridData = (query: string,
    status: number,
    _cat?: number,
    _reg?: number,
    _type?: number,
    date?: string,
    proposalType?: number,
    startDate?: string,
    endDate?: string) => {
    const lowerCaseQuery = query.toLowerCase();
    const filtered = noteDataList.filter((item) => {
      const matchesQuery =
        item.notes.toLowerCase().includes(lowerCaseQuery) ||
        item.createdName?.toLowerCase().includes(lowerCaseQuery) ||
        item.updatedName?.toLowerCase().includes(lowerCaseQuery);
      const matchDate = date
        ? (
          item.createdAt?.local().format("YYYY-MM-DD") === dayjs(date).format("YYYY-MM-DD") ||
          item.updatedAt?.local().format("YYYY-MM-DD") === dayjs(date).format("YYYY-MM-DD")
        )
        : true;

      // 📅 Date Range filter
      let matchDateRange = true;

      if (startDate || endDate) {
        const created = item.createdAt?.local().format("YYYY-MM-DD");
        const updated = item.updatedAt?.local().format("YYYY-MM-DD");

        const s = startDate ? dayjs(startDate).format("YYYY-MM-DD") : null;
        const e = endDate ? dayjs(endDate).format("YYYY-MM-DD") : null;
       
        const checkDateInRange = (d: string) => {
          if (!d) return false;

          if (s && e) return d >= s && d <= e;
          if (s) return d >= s;
          if (e) return d <= e;

          return true;
        };

        matchDateRange =
          checkDateInRange(created) || checkDateInRange(updated);
      }


      const matchesStatus = status === -1 || status === 2 || item.status === status;
      return matchesQuery && matchDate && matchDateRange && matchesStatus;
    });

    const newStatus = status === 0 ? [0] : (status === -1 || status === 2) ? [0, 1] : [1];
    setSelectedStatus(newStatus)
    showGrid(filtered, newStatus);
  };

  const handleNoteSubmit = async () => {
    const newErrors = { description: '' };
    if (!formData.notes?.trim()) {
      newErrors.description = validationMessages.notesComments_required;
      return setErrors(newErrors);
    } else if (formData.notes.length > 5000) {
      newErrors.description = validationMessages.common_maxLength("Notes", 5000);
      return setErrors(newErrors);
    }

    let response: ApiResponse<any>;

    if (selectedNoteRow) {
      const UpdatedNote = {
        ...formData,
        notes: formData.notes,
        status: selectedNoteRow ? (selectedNoteRow.Status === 'Active' ? 1 : 0) : 1,
        updatedId: userId,
        createdId: userId,
        entityTypeId: resourceId, // Assuming resourceId is the proposalId
        noteId: selectedNoteRow.id   // Assuming resourceId is the proposalId
      };

      response = await execute('put', `Note`, UpdatedNote);

    } else {

      const newNote = {
        notes: formData.notes,
        status: 1, // Default to Active
        createdId: userId,
        entityId: resourceId, // Assuming resourceId is the proposalId
        entityTypeId: entityTypeId,
      }
      response = await execute('post', `Note`, newNote);
    }

    if (response.error) {
      setToast({ open: true, message: response.error, type: "error" });
    } else {
      setToast({
        open: true,
        message: typeof response.data === "string" ? response.data : response.data.message || "Note added successfully",
        type: "success"
      });
      setFormData({ notes: '' });
      setSelectedNoteRow(null);
      fetchAllNotes();
    }
  };

  const handleEdit = (note: any) => {
    setFormData({ notes: note["Notes/Comments"] });
    setSelectedNoteRow({ ...note, noteId: note.id });
  };

  const handleToggleState = (row: any) => {
    setIsOn(row.Status === 'Active');
    setSelectedNoteRow(row);
    setDialogOpen(true);
  };

  const handleDialogConfirm = async () => {
    if (!selectedNoteRow) return;
    //const { data } = await execute('get', getNoteByIdUrl(selectedNoteRow.id));

    // if(!data) {
    //   setToast({ open: true, message: "Failed to fetch note details.", type: "error" });
    //   return;
    // }
    const updatedNote = {
      [`noteId`]: selectedNoteRow.id, // dynamic note ID field (e.g. proposalNoteId/projectNoteId)
      notes: selectedNoteRow["Notes/Comments"],
      status: selectedNoteRow.Status === 'Active' ? 0 : 1,
      updatedId: userId
    };

    const response = await execute('put', `Note`, updatedNote);
    if (response.error) {
      setToast({ open: true, message: "Error updating note status", type: "error" });
    } else {
      setToast({ open: true, message: "Note status updated successfully", type: "success" });
      fetchAllNotes();
    }
    setDialogOpen(false);
    setSelectedNoteRow(null);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    setSelectedNoteRow(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}>
      <Grid2
        container
        spacing={2}
        sx={{ mb: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}
      >
        <Grid2 size={{ xs: 12, md: 8 }}>
          <Typography
            sx={{ color: '#000', fontWeight: 600, fontSize: '1.5rem', mb: 0.5, whiteSpace: 'nowrap' }}
          >
            {`${title} Notes/Comments`}
          </Typography>
          <Typography sx={{ whiteSpace: 'nowrap', color: '#7a7699' }}>
            {`Add and manage notes or comments related to this ${title}`}
          </Typography>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end', my: "auto" }}>
          <CustomButton
            capsule
            disabled={loading}
            sx={{
              mt: 1,
              mb: 2,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              minWidth: '200px',
              width: { xs: '100%', md: 'auto' },
              px: 4,
            }}
            onClick={handleNoteSubmit}
          >
            {selectedNoteRow ? 'Update Notes/Comments' : 'Add Notes/Comments'}
          </CustomButton>
        </Grid2>

         <Grid2 size={{ xs: 12, md: 12, lg: 12 }}>
          <CustomTextField
            label={`${title} Notes/Comments`}
            hideLabel
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            isTextArea
            sx={{
              '& .MuiOutlinedInput-root': {
                borderColor: formData.notes ? '#1e66ab' : 'default',
                boxShadow: formData.notes ? '0 0 5px rgba(30, 102, 171, 0.5)' : 'none',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: formData.notes ? 'rgba(150, 193, 33, 0.75)' : 'default',
                borderWidth: formData.notes ? '2px' : '1px',
              },
              '& .MuiInputBase-input': { backgroundColor: 'white' },
              '& textarea': { minHeight: '50px' }
            }}
          />
          {errors.description && (
            <Typography className="validationMessage" role="alert">{errors.description}</Typography>
          )}
        </Grid2>
        </Grid2>

      <FilterCard showNotesComments title={`${title} Notes`} showDateRange={true} actionText="" searchbarText={searchbarText} handleSearch={handleSearchGridData} />
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
          <CircularProgress size={28} />
        </Box>
      ) : (noteDataList.length > 0) ? (
        <Box sx={{display: "flex", flexDirection: "column", minHeight: "auto", maxHeight: "600px"}}>
        <CustomDataTable
          rows={filteredData}
          actionText="Note"
          sortOptions={sortOptions}
          onRowEdit={handleEdit}
          onToggleClick={handleToggleState}
          isDialogOpen={dialogOpen}
          isToggleOn={isOn}
          handleCancel={handleDialogCancel}
          handleConfirm={handleDialogConfirm}
          // gridHeight='calc(100vh - 200px)'
          noResultMessage={validationMessages.common_RecordNotFound(`${title} Notes`)}
        />
        </Box>
      ) : dataLoaded ?
        (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <Typography sx={{ color: Default_Text_Color, marginTop: '2rem' }}> There are no Notes/Comments to display</Typography>
          </Box>
        ) : null}

      <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
    </Box>
  );
};

export default NotesComponent;
